"use client";
import { useForm } from "react-hook-form";
import { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";

import Appointment from "./Appointment";
import AWOappointment from "./AWOappointment";

interface AppointmentFormValues {
  refNo: string;
  date: string;
  name: string;
  address: string;
  designation: string;
  joining_Date: string;
  Joining_Year: string;
  State: string;
  DOB: string;
  Last_date: string;
  Reporting_RWO: string;
  district: string;
  letterType?: string;
}

// ✅ Appointment Letter Types
const appointmentTypes = ["RWO", "DWO", "AWO"];

// ✅ Fields per appointment type (simple string arrays)
const fieldsMap: { [key: string]: string[] } = {
  RWO: [
    "refNo",
    "date",
    "name",
    "address",
    "joining_Date",
    "Joining_Year",
    "State",
    "DOB",
    "Last_date",
  ],
  DWO: [
    "refNo",
    "date",
    "name",
    "address",
    "joining_Date",
    "Joining_Year",
    "State",
    "DOB",
  ],
  AWO: ["refNo", "date", "name", "address", "joining_Date", "Joining_Year", "Last_date", "State", "district", "DOB", "Reporting_RWO"],
};

function AppointmentContent() {
  const form = useForm<AppointmentFormValues>({
    defaultValues: {
      refNo: "[Ref No]",
      date: "[Date]",
      name: "[Name]",
      address: "[Address]",
      designation: "[Designation]",
      joining_Date: "[Joining Date]",
      Joining_Year: "[Joining Year]",
      State: "[State]",
      DOB: "[DOB]",
      Last_date: "[Last Date]",
      Reporting_RWO: "[Reporting_RWO]",
      letterType: "",
    },
  });

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [submittedData, setSubmittedData] =
    useState<AppointmentFormValues | null>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    documentTitle: submittedData
      ? `${submittedData.name.replace(/\s+/g, "_")}_${selectedType}_Appointment_Letter`
      : "Appointment_Letter",

      pageStyle:`
        
      @page{
        size: A4;
        margin: 0;
      }
        body{
        zoom: 1.17;
        }

      `,

  });

  const handleCreateLetter = () => {
    const type = form.getValues("letterType");
    if (!type) {
      alert("Please select appointment type first!");
      return;
    }
    setSelectedType(type);
  };

  const onSubmit = (values: AppointmentFormValues) => {
    setSubmittedData(values);
  };

  // ✅ Dynamic fields rendering
  const renderFields = () => {
    if (!selectedType) return null;
    const fields = fieldsMap[selectedType] || [];

    return fields.map((fieldName) => (
      <FormField
        key={fieldName}
        control={form.control}
        name={fieldName as keyof AppointmentFormValues}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="capitalize">
              {fieldName.replace(/_/g, " ")}
            </FormLabel>
            <FormControl>
              <Input
                type={fieldName.toLowerCase().includes("date") || fieldName.toLowerCase().includes("DOB") ? "date" : "text"}
                placeholder={`Enter ${fieldName}`}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    ));
  };

  return (
    <>
      {/* Appointment Letter Type Selector */}
      <Form {...form}>
        <FormField
          control={form.control}
          name="letterType"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>Select Appointment Letter</FormLabel>
                <Button type="button" onClick={handleCreateLetter}>
                  Create Letter
                </Button>
              </div>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose Role" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Form>

      {/* Form stays visible */}
      {selectedType && (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-4"
          >
            {renderFields()}

            <Button type="submit" className="mt-4">
              Generate {selectedType} Appointment Letter
            </Button>
          </form>
        </Form>
      )}

      {/* Preview + Download */}
      {submittedData && (
        <>
          <div className="mt-6">
            <Button onClick={() => handlePrint(() => componentRef.current)}>
              Download / Print Letter
            </Button>
          </div>

          <div ref={componentRef} className="mt-8 p-12 rounded-lg bg-white">
            {selectedType ==="AWO" ? (
            <AWOappointment {...submittedData} />): (
            <Appointment {...submittedData}/>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default AppointmentContent;
